import { FileIdsValue, SelectMultipleValue } from '@kaizenplatform/hugot-entity';
import gql from 'graphql-tag';

import GraphQLClient, { GraphQLOperationFailed } from '../../adapter/GraphQLClient';
import Language from '../../entity/Language';
import {
  CreateProjectIssueCommentMutation,
  CreateProjectIssueMutation,
  DeleteProjectIssueCommentMutation,
  GetProjectByIdQuery,
  UpdateProjectIssueCommentMutation,
} from '../../graphql/generated/graphql';
import { jsonToDynamicSchemaValue } from '../../utility/graphql/project';
import { DigNonNullable } from '../../utilityTypes/DigNonNullable';
import { projectForCreationSetDetailFragment } from './CreationSetWebApi';

export type ProjectType = NonNullable<GetProjectByIdQuery['project']> & {
  creativeBriefValue?: {
    definition: DigNonNullable<GetProjectByIdQuery, ['project', 'creativeBriefDefinition']>;
    values: Map<string, string | number | boolean | SelectMultipleValue | FileIdsValue | null>;
  };
  assetCategoryValue?: {
    definition: DigNonNullable<GetProjectByIdQuery, ['project', 'assetCategoryDefinition']>;
    values: Map<string, string | number | boolean | SelectMultipleValue | FileIdsValue | null>;
  };
};

export const creationSetForProjectFragment = gql`
  fragment CreationSetForProject on CreationSet {
    id
    offerId
    creativeFormats {
      id
      network {
        id
        key
        name
      }
      placement {
        id
        key
        name
      }
      creativeType {
        id
        key
        name
      }
      attribute {
        aspectRatio {
          supportingAspectRatioId
          aspectRatio {
            width
            height
          }
          size {
            width
            height
          }
        }
        playbackTime {
          supportingPlaybackTimeId
          seconds
        }
      }
      selectedPricingMerchandiseOptions {
        id
        name
        displayToOptimizer
        helpUrlForOptimizer
      }
      resizeBaseId
    }
    amount
    expressDueDays
    inspectionPassedCreations {
      id
      state
      proposed
      createdAt
      assignee {
        id
        name
        profileImageUrl
      }
      latestProposedDeliverable {
        id
        state
        createdAt
        stateUpdatedAt
        intent
        creative {
          id
          file {
            id
            originalFileName
            url
            size
            mimeType
            fastStart {
              enabled
            }
            attribute {
              aspectRatio {
                size {
                  width
                  height
                }
              }
              playbackTime {
                seconds
              }
            }
          }
        }
        attachedThumbnail {
          id
          file {
            id
            url
            originalFileName
            mimeType
            size
            fastStart {
              enabled
            }
            attribute {
              aspectRatio {
                size {
                  width
                  height
                }
              }
              playbackTime {
                seconds
              }
            }
          }
        }
      }
    }
  }
`;

class ProjectWebApi {
  private graphqlClient: GraphQLClient;

  constructor({ graphqlClient }: { graphqlClient: GraphQLClient }) {
    this.graphqlClient = graphqlClient;
  }

  public getById = async (id: string, language: Language): Promise<NonNullable<GetProjectByIdQuery['project']>> => {
    return this._getById(language, id, undefined);
  };
  // TODO: 多分不要な情報も一杯取ってる
  public getByCreationSetId = async (
    creationSetId: string,
    language: Language,
  ): Promise<NonNullable<GetProjectByIdQuery['project']>> => {
    return this._getById(language, undefined, creationSetId);
  };

  private _getById = async (language: Language, projectId?: string, creationSetId?: string): Promise<ProjectType> => {
    try {
      const result: GetProjectByIdQuery = await this.graphqlClient.query(
        gql`
          ${creationSetForProjectFragment}
          ${projectForCreationSetDetailFragment}
          query getProjectById($projectId: ID, $creationSetId: ID, $language: ID) {
            project(id: $projectId, creationSetId: $creationSetId, language: $language) {
              ...ProjectForCreationSetDetail
              originId
              parentId
              state
              createdBy {
                id
                name
                profileImageUrl
              }
              creationSet {
                ...CreationSetForProject
              }
              createdAt
              submittedAt
            }
          }
        `,
        { projectId, creationSetId, language },
      );

      if (result.project) {
        const project: ProjectType = result.project;

        project.creativeBriefValue = jsonToDynamicSchemaValue(
          project.creativeBriefValues,
          project.creativeBriefDefinition,
        );

        project.assetCategoryValue = jsonToDynamicSchemaValue(
          project.assetCategoryValues,
          project.assetCategoryDefinition,
        );

        return project;
      } else {
        throw new Error('project is blank');
      }
    } catch (err) {
      if (err instanceof GraphQLOperationFailed && err.type === 'KaizenAd::Exceptions::Optimizer::Project::NotFound') {
        throw new ProjectNotFound({ id: projectId || creationSetId || '' });
      }
      throw err;
    }
  };

  public createProjectIssue = async (
    projectId: string,
    name: string,
    body: string,
  ): Promise<NonNullable<CreateProjectIssueMutation['createProjectIssue']>> => {
    const result: CreateProjectIssueMutation = await this.graphqlClient.query(
      gql`
        mutation createProjectIssue($projectId: ID!, $name: String!, $body: String!) {
          createProjectIssue(projectId: $projectId, name: $name, body: $body) {
            id
            name
            comments {
              id
              body
              createdAt
              updatedAt
              user {
                ... on UserDirector {
                  id
                  name
                  email
                  profileImageUrl
                }
                ... on UserOptimizer {
                  id
                  name
                  email
                  profileImageUrl
                }
              }
            }
          }
        }
      `,
      { projectId, name, body },
    );

    if (result.createProjectIssue) {
      return result.createProjectIssue;
    } else {
      throw new Error('createProjectIssue is blank');
    }
  };

  public createProjectIssueComment = async (
    issueId: string,
    body: string,
  ): Promise<NonNullable<CreateProjectIssueCommentMutation['createProjectIssueComment']>> => {
    const result: CreateProjectIssueCommentMutation = await this.graphqlClient.query(
      gql`
        mutation createProjectIssueComment($issueId: ID!, $body: String!) {
          createProjectIssueComment(issueId: $issueId, body: $body) {
            id
            body
            createdAt
            updatedAt
            user {
              ... on UserDirector {
                id
                name
                email
                profileImageUrl
              }
              ... on UserOptimizer {
                id
                name
                email
                profileImageUrl
              }
            }
          }
        }
      `,
      { issueId, body },
    );
    if (result.createProjectIssueComment) {
      return result.createProjectIssueComment;
    } else {
      throw new Error('createProjectIssueComment is blank');
    }
  };

  public updateProjectIssueComment = async (
    commentId: string,
    body: string,
  ): Promise<NonNullable<UpdateProjectIssueCommentMutation['updateProjectIssueComment']>> => {
    const result: UpdateProjectIssueCommentMutation = await this.graphqlClient.query(
      gql`
        mutation updateProjectIssueComment($commentId: ID!, $body: String!) {
          updateProjectIssueComment(commentId: $commentId, body: $body) {
            id
            body
            createdAt
            updatedAt
            user {
              ... on UserDirector {
                id
                name
                email
                profileImageUrl
              }
              ... on UserOptimizer {
                id
                name
                email
                profileImageUrl
              }
            }
          }
        }
      `,
      { commentId, body },
    );

    if (result.updateProjectIssueComment) {
      return result.updateProjectIssueComment;
    } else {
      throw new Error('updateProjectIssueComment is blank');
    }
  };

  public deleteProjectIssueComment = async (
    commentId: string,
  ): Promise<NonNullable<DeleteProjectIssueCommentMutation['deleteProjectIssueComment']>> => {
    const result: DeleteProjectIssueCommentMutation = await this.graphqlClient.query(
      gql`
        mutation deleteProjectIssueComment($commentId: ID!) {
          deleteProjectIssueComment(commentId: $commentId) {
            id
            name
            comments {
              id
              body
              createdAt
              updatedAt
              user {
                ... on UserDirector {
                  id
                  name
                  email
                  profileImageUrl
                }
                ... on UserOptimizer {
                  id
                  name
                  email
                  profileImageUrl
                }
              }
            }
          }
        }
      `,
      { commentId },
    );

    if (result.deleteProjectIssueComment) {
      return result.deleteProjectIssueComment;
    } else {
      throw new Error('deleteProjectIssueComment is blank');
    }
  };
}
export class ProjectNotFound extends Error {
  public readonly name = 'ProjectNotFound';
  public readonly message: string;
  public readonly id: string;

  constructor({ id }: { id: string }) {
    super();

    this.id = id;
    this.message = `The project (id = ${id}) is not found.`;
  }
}

export default ProjectWebApi;
